import React, { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { MuiTelInput, matchIsValidTel } from "mui-tel-input";
import { useTranslation } from "react-i18next";
import { getAgents } from "../../api/agents";
import { createManualBooking } from "../../api/bookings";
import { getCityDisplay } from "../../utils/city";

const emptyPassenger = () => ({
  name: "",
  surname: "",
  type: "adult",
});

const AddTripPassengerDialog = ({ open, onClose, trip, onCreated }) => {
  const { t, i18n } = useTranslation("common");
  const lang = i18n.language?.startsWith("en") ? "en" : "uk";
  const stops = (trip?.route?.stops || []).filter((stop) => stop.city);
  const [fromStop, setFromStop] = useState(null);
  const [toStop, setToStop] = useState(null);
  const [passenger, setPassenger] = useState(emptyPassenger());
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("not_paid");
  const [fundsHolder, setFundsHolder] = useState("sv");
  const [agent, setAgent] = useState(null);
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setPassenger(emptyPassenger());
    setPhone("");
    setEmail("");
    setStatus("not_paid");
    setFundsHolder("sv");
    setAgent(null);
    setError("");
    setFromStop(stops[0] || null);
    setToStop(stops[stops.length - 1] || null);
    getAgents({ assignable: 1 })
      .then((rows) => setAgents(Array.isArray(rows) ? rows : []))
      .catch(() => setAgents([]));
  }, [open, trip?.id]);

  const cityName = (stop) =>
    stop?.city?.name || (typeof stop?.city === "string" ? stop.city : "");

  const canSave =
    cityName(fromStop) &&
    cityName(toStop) &&
    cityName(fromStop) !== cityName(toStop) &&
    passenger.name.trim().length >= 2 &&
    passenger.surname.trim().length >= 2 &&
    matchIsValidTel(phone);

  const save = async () => {
    if (!canSave || !trip?.id) return;
    setSaving(true);
    setError("");
    try {
      const payload = {
        trip_id: trip.id,
        from_city: cityName(fromStop),
        to_city: cityName(toStop),
        adults: 1,
        children: passenger.type === "child" ? 1 : 0,
        passengers: [
          {
            name: passenger.name.trim(),
            surname: passenger.surname.trim(),
            type: passenger.type,
          },
        ],
        buyer_phone: phone.replace(/\s+/g, ""),
        buyer_email: email.trim() || undefined,
        status,
        funds_holder: fundsHolder,
      };
      if (agent?.id) payload.selling_organization_id = agent.id;
      await createManualBooking(payload);
      onCreated();
      onClose();
    } catch (err) {
      const data = err?.response?.data;
      setError(
        (typeof data === "string" && data) ||
          data?.details ||
          data?.error ||
          t("admin.tripAddPassengerError")
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("admin.tripAddPassenger")}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Autocomplete
            options={stops}
            value={fromStop}
            onChange={(_e, value) => setFromStop(value)}
            getOptionLabel={(stop) => getCityDisplay(stop.city, lang)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.routesFareFrom")} />
            )}
          />
          <Autocomplete
            options={stops}
            value={toStop}
            onChange={(_e, value) => setToStop(value)}
            getOptionLabel={(stop) => getCityDisplay(stop.city, lang)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} label={t("admin.routesFareTo")} />
            )}
          />
          <TextField
            label={t("form.name")}
            value={passenger.name}
            onChange={(event) =>
              setPassenger((row) => ({ ...row, name: event.target.value }))
            }
          />
          <TextField
            label={t("form.surname")}
            value={passenger.surname}
            onChange={(event) =>
              setPassenger((row) => ({ ...row, surname: event.target.value }))
            }
          />
          <FormControl>
            <InputLabel>{t("adminDetails.passengerType")}</InputLabel>
            <Select
              label={t("adminDetails.passengerType")}
              value={passenger.type}
              onChange={(event) =>
                setPassenger((row) => ({ ...row, type: event.target.value }))
              }
            >
              <MenuItem value="adult">{t("adminDetails.adult")}</MenuItem>
              <MenuItem value="child">{t("adminDetails.child")}</MenuItem>
            </Select>
          </FormControl>
          <MuiTelInput
            defaultCountry="UA"
            value={phone}
            onChange={setPhone}
            label={t("checkout.yourPhone")}
          />
          <TextField
            label={t("checkout.yourEmail")}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          <Autocomplete
            options={agents}
            value={agent}
            onChange={(_e, value) => setAgent(value)}
            getOptionLabel={(row) => row.name || row.display_name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText={t("admin.tripPassengerSourceEmpty")}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("admin.tripPassengerSource")}
                helperText={t("admin.tripPassengerSourceHint")}
              />
            )}
          />
          <FormControl>
            <InputLabel>{t("admin.fundsHolder")}</InputLabel>
            <Select
              label={t("admin.fundsHolder")}
              value={fundsHolder}
              onChange={(event) => setFundsHolder(event.target.value)}
            >
              <MenuItem value="sv">{t("admin.fundsHolderSv")}</MenuItem>
              <MenuItem value="agent">{t("admin.fundsHolderAgent")}</MenuItem>
              <MenuItem value="carrier">{t("admin.fundsHolderCarrier")}</MenuItem>
            </Select>
          </FormControl>
          <FormControl>
            <InputLabel>{t("admin.status")}</InputLabel>
            <Select
              label={t("admin.status")}
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <MenuItem value="not_paid">{t("status.not_paid")}</MenuItem>
              <MenuItem value="paid">{t("status.paid")}</MenuItem>
            </Select>
          </FormControl>
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          {t("admin.cancel")}
        </Button>
        <Button variant="contained" onClick={save} disabled={!canSave || saving}>
          {t("admin.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddTripPassengerDialog;
